import { e, intersect, max, smaller } from 'mathjs';
import { edgeIntersectionTolerance } from '../config';
import { Node, Edge, MaxFlowProblem } from '../typings';
import { stepEdmondsKarp } from './EdmondsKarpSolver';

const { sqrt } = Math;

function distance(a: number[], b: number[]): number {
  const c0 = a[0] - b[0];
  const c1 = a[1] - b[1];
  return sqrt(c0 ** 2 + c1 ** 2);
}

export function generateProblemWithLocation(
  noNodes: number,
  maxCapacity: number,
  retries = 0
): MaxFlowProblem {
  // The area, which all nodes together shall claim. I. e. nodes claim an area around them in which no other node shall be generated.
  const minimumNodeDistance = 0.3 * (1 / noNodes) ** 0.5; // 0.3 is a safety margin for efficient placement

  const time: number[] = [];
  time.push(performance.now());
  // Generate nodes
  const nodes: Node[] = [];
  for (let i = 0; i < noNodes; i += 1) {
    let addNode = false;
    let placementAttempts = 0;
    while (!addNode) {
      placementAttempts += 1;
      addNode = true;
      const newNodePos: number[] = [];
      for (let j = 0; j < 2; j += 1) {
        newNodePos.push(Math.random());
      }
      for (let j = 0; j < i; j += 1) {
        const existingNode = nodes[j];
        if (distance(existingNode.position, newNodePos) < minimumNodeDistance) addNode = false;
      }
      if (addNode) nodes.push({ position: newNodePos as [number, number] });
      if (placementAttempts > 1000) {
        throw new Error('Cannot place node with sufficient distance to other nodes.');
      }
    }
  }

  time.push(performance.now());
  // Generate edge candidates
  let edgeCandidates: {
    source: number;
    target: number;
    distance: number;
  }[] = [];
  for (let i = 0; i < noNodes; i += 1) {
    for (let j = 0; j < i; j += 1) {
      edgeCandidates.push({
        source: i,
        target: j,
        distance: distance(nodes[i].position, nodes[j].position) as number
      });
    }
  }

  time.push(performance.now());
  // Select edges for a planar graph from edge candidates
  edgeCandidates = edgeCandidates.sort((a, b) => a.distance - b.distance);
  const edges: {
    source: number;
    target: number;
    maxCapacity: number;
    residualCapacity: number;
  }[] = [];
  for (const edgeCandidate of edgeCandidates) {
    let addEdge = true;
    for (const edge of edges) {
      // todo: Extract into function for testing
      const w = nodes[edgeCandidate.source].position;
      const x = nodes[edgeCandidate.target].position;
      const y = nodes[edge.source].position;
      const z = nodes[edge.target].position;
      const z1my1 = z[1] - y[1];
      const w1mx1 = w[1] - x[1];
      let p = (z[0] - y[0]) / z1my1;
      let a = (y[0] - x[0] + (x[1] - y[1]) * p) / (w[0] - x[0] - w1mx1 * p);
      let b = (x[1] - y[1] + a * w1mx1) / z1my1;
      // isNaN(a) or isNaN(b) is very unlikely
      if (Number.isNaN(a) || Number.isNaN(b)) {
        const x0mw0 = x[0] - w[0];
        const z0my0 = z[0] - y[0];
        p = (x[1] - w[1]) / x0mw0;
        b = (w[1] - y[1] + (y[0] - w[0]) * p) / (z[1] - y[1] - z0my0 * p);
        a = (y[0] + z0my0 * b - w[0]) / x0mw0;
        if (Number.isNaN(a) || Number.isNaN(b)) {
          // mathjs intersect is rather slow, however it handles edges cases
          // -> use custom higher performance code for likely cases and fall back to mathjs if NaN
          const s = intersect(w, x, y, z);
          // todo: parallel and intersecting
          // todo: How to detect edges, that almost intersect?
          if (
            s &&
            ((smaller(w[0], s[0]) && smaller(s[0], x[0])) ||
              (smaller(x[0], s[0]) && smaller(s[0], w[0]))) &&
            ((smaller(y[0], s[0]) && smaller(s[0], z[0])) ||
              (smaller(z[0], s[0]) && smaller(s[0], y[0])))
          ) {
            addEdge = false;
            break;
          }
        }
      }
      addEdge = a <= 0 || a >= 1 || b <= 0 || b >= 1; // todo: tolerance
      if (!addEdge) break;
    }

    if (addEdge) {
      // 0 <= Math.random() < 1
      let capacity = Math.floor(Math.random() * maxCapacity) + 1;
      edges.push({
        source: edgeCandidate.source,
        target: edgeCandidate.target,
        maxCapacity: capacity,
        residualCapacity: capacity
      });
      capacity = Math.floor(Math.random() * maxCapacity) + 1;
      edges.push({
        source: edgeCandidate.target,
        target: edgeCandidate.source,
        maxCapacity: capacity,
        residualCapacity: capacity
      });
    }
  }

  time.push(performance.now());
  // Remove edges, which lead to small polygons in order to guarantee space for labels
  let removedEdge;
  do {
    // Find cycles
    const visited: number[] = [];
    const queue: number[] = [0];
    const cycles: number[][] = [];
    const nodeParents: number[] = Array.from(nodes, () => -1);
    while (queue.length > 0) {
      for (const edge of edges) {
        if (edge.source === queue[0]) {
          if (visited.includes(edge.target)) {
            nodeParents[edge.target] = edge.source;
            const n = edge.source;
            const cycle: number[] = [n];
            // while (nodeParents[n] !== edge.source) {
            //  console.log('nodeParents :>> ', nodeParents);
            //  console.log('n :>> ', n);
            //  cycle.push(nodeParents[n]);
            //  n = nodeParents[n];
            // }
            cycles.push();
          } else {
            nodeParents[edge.target] = edge.source;
            queue.push(edge.target);
          }
        }
      }
      visited.push(queue.pop() as number);
    }
  } while (removedEdge);

  // Transform edges to sparse arrays
  time.push(performance.now());

  const sortedEdges: Edge[][] = [];
  for (const edge of edges) {
    if (!sortedEdges[edge.source]) sortedEdges[edge.source] = [];
    sortedEdges[edge.source][edge.target] = {
      maxCapacity: edge.maxCapacity,
      residualCapacity: edge.residualCapacity
    };
  }

  time.push(performance.now());
  const cachedTargetKeys = [];
  for (let u = 0; u < sortedEdges.length; u += 1) {
    cachedTargetKeys[u] = [] as number[];
    for (let v = 0; v < sortedEdges.length; v += 1) {
      if (sortedEdges[u][v]) cachedTargetKeys[u].push(v);
    }
  }

  time.push(performance.now());
  // Choose some s and t.
  // Check if this choice results in s and t being part of a min cut.
  // Repeat.
  function getFlowValue(p: MaxFlowProblem): number {
    let f = 0;
    for (const v of p.graph.cachedTargetKeys[p.source]) {
      f += p.graph.edges[p.source][v].maxCapacity - p.graph.edges[p.source][v].residualCapacity;
      f -= p.graph.edges[v][p.source].maxCapacity - p.graph.edges[v][p.source].residualCapacity;
    }
    return f;
  }

  let haveValidST = false;
  let s = 0;
  let t = 0;
  let p = JSON.parse(
    JSON.stringify({
      graph: {
        nodes,
        edges: sortedEdges,
        cachedTargetKeys
      },
      source: s,
      target: t
    })
  ) as MaxFlowProblem;
  const origEdges = JSON.parse(JSON.stringify(p.graph.edges));
  for (s = 0; s < noNodes && !haveValidST; s += 1) {
    for (t = noNodes - 1; t >= 0 && !haveValidST; t -= 1) {
      if (s === t) continue;
      p.source = s;
      p.target = t;
      for (let u = 0; u < p.graph.nodes.length; u += 1) {
        for (const v of cachedTargetKeys[u]) {
          p.graph.edges[u][v].maxCapacity = origEdges[u][v].maxCapacity;
          p.graph.edges[u][v].residualCapacity = origEdges[u][v].residualCapacity;
        }
      }
      haveValidST = true;
      let done = false;
      // Increase capacity for source and target edges to max in order to make them being part of a min cut less likely.
      // Check whether there is a direct edge from source to target as that will be part of a min cut.
      for (const v of p.graph.cachedTargetKeys[p.source]) {
        if (v === p.target) {
          haveValidST = false;
          break;
        }
        p.graph.edges[p.source][v].maxCapacity = maxCapacity;
        p.graph.edges[p.source][v].residualCapacity = maxCapacity;
      }
      if (!haveValidST) continue;
      for (const u of cachedTargetKeys[p.target]) {
        if (u === p.source) {
          haveValidST = false;
          break;
        }
        p.graph.edges[u][p.target].maxCapacity = maxCapacity;
        p.graph.edges[u][p.target].residualCapacity = maxCapacity;
      }
      if (!haveValidST) continue;
      // Check whether there is a path of length 2 from source to target as edges on that path would be part of a min cut.
      // Somehow this has slightly worse performance than not doing it.
      // for (const v of p.graph.cachedTargetKeys[p.source]) {
      //  if (!haveValidST) break;
      //  for (const u of cachedTargetKeys[p.target]) {
      //    if (u === v) {
      //      haveValidST = false;
      //      break;
      //    }
      //  }
      // }
      if (!haveValidST) continue;
      while (!done) {
        [p, done] = stepEdmondsKarp(p, [], [], 0, [], true, 0, 0);
      }
      const unmodifiedFlowValue = getFlowValue(p);
      for (let u = 0; u < p.graph.nodes.length; u += 1) {
        for (const v of cachedTargetKeys[u]) {
          p.graph.edges[u][v].residualCapacity = p.graph.edges[u][v].maxCapacity;
        }
      }
      for (const v of cachedTargetKeys[s]) {
        p.graph.edges[s][v].maxCapacity -= 1;
        p.graph.edges[s][v].residualCapacity -= 1;
      }
      done = false;
      while (!done) {
        [p, done] = stepEdmondsKarp(p, [], [], 0, [], true, 0, 0);
      }
      if (unmodifiedFlowValue !== getFlowValue(p)) {
        haveValidST = false;
      }
      for (const v of cachedTargetKeys[s]) {
        p.graph.edges[s][v].maxCapacity += 1;
        p.graph.edges[s][v].residualCapacity += 1;
      }
      if (!haveValidST) continue;
      for (let u = 0; u < p.graph.nodes.length; u += 1) {
        for (const v of cachedTargetKeys[u]) {
          p.graph.edges[u][v].residualCapacity = p.graph.edges[u][v].maxCapacity;
        }
      }
      for (const u of cachedTargetKeys[t]) {
        p.graph.edges[u][t].maxCapacity -= 1;
        p.graph.edges[u][t].residualCapacity -= 1;
      }
      done = false;
      while (!done) {
        [p, done] = stepEdmondsKarp(p, [], [], 0, [], true, 0, 0);
      }
      if (unmodifiedFlowValue !== getFlowValue(p)) {
        haveValidST = false;
      }
      for (const u of cachedTargetKeys[t]) {
        p.graph.edges[u][t].maxCapacity += 1;
        p.graph.edges[u][t].residualCapacity += 1;
      }
    }
  }
  time.push(performance.now());
  s -= 1;
  t += 1;
  if (!haveValidST) {
    if (retries < Infinity) return generateProblemWithLocation(noNodes, maxCapacity, retries + 1);
    throw Error('Could not generate problem instance.');
  }
  for (const v of cachedTargetKeys[s]) {
    sortedEdges[s][v].maxCapacity = maxCapacity;
    sortedEdges[s][v].residualCapacity = maxCapacity;
  }
  for (const u of cachedTargetKeys[t]) {
    sortedEdges[u][t].maxCapacity = maxCapacity;
    sortedEdges[u][t].residualCapacity = maxCapacity;
  }
  time.push(performance.now());
  // for (let i = 1; i < time.length; i += 1) {
  //   console.log(time[i] - time[i - 1]);
  // }

  return {
    graph: {
      nodes,
      edges: sortedEdges,
      cachedTargetKeys
    },
    source: s,
    target: t
  };
}
